import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/modules/auth";
import { PendingApprovalScreen } from "@/components/shared/PendingApprovalScreen";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { FlowErrorBoundary } from "@/components/shared/FlowErrorBoundary";

// Eagerly loaded pages (landing, auth, public routes)
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfService from "./pages/legal/TermsOfService";
import PricingPage from "./pages/PricingPage";

// Lazy loaded layouts
const CustomerDashboardLayout = lazy(() => import("./layouts/CustomerDashboardLayout"));
const TradieDashboardLayout = lazy(() => import("./layouts/TradieDashboardLayout").then(m => ({ default: m.TradieDashboardLayout })));
const AdminDashboardLayout = lazy(() => import("./layouts/AdminDashboardLayout"));
const PMDashboardLayout = lazy(() => import("./layouts/PMDashboardLayout"));
const TenantDashboardLayout = lazy(() => import("./layouts/TenantDashboardLayout"));

// Lazy loaded customer pages
const CustomerDashboard = lazy(() => import("./pages/CustomerDashboard"));
const NewJobPage = lazy(() => import("./pages/NewJobPage"));

// Lazy loaded tradie pages
const TradieDashboard = lazy(() => import("./pages/TradieDashboard").then(m => ({ default: m.TradieDashboard })));
const TradieJobFeed = lazy(() => import("./pages/TradieJobFeed").then(m => ({ default: m.TradieJobFeed })));
const TradieMyJobs = lazy(() => import("./pages/TradieMyJobs").then(m => ({ default: m.TradieMyJobs })));
const TradieEarnings = lazy(() => import("./pages/TradieEarnings").then(m => ({ default: m.TradieEarnings })));
const TradieSettings = lazy(() => import("./pages/TradieSettings").then(m => ({ default: m.TradieSettings })));

// Lazy loaded shared pages
const JobDetailsPage = lazy(() => import("./pages/JobDetailsPage").then(m => ({ default: m.JobDetailsPage })));

// Lazy loaded admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminJobs = lazy(() => import("./pages/admin/AdminJobs"));
const AdminJobEvents = lazy(() => import("./pages/admin/AdminJobEvents"));
const AdminAlerts = lazy(() => import("./pages/admin/AdminAlerts"));
const AdminPricing = lazy(() => import("./pages/admin/AdminPricing"));
const AdminEnterprisePricing = lazy(() => import("./pages/admin/AdminEnterprisePricing"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminOps = lazy(() => import("./pages/admin/AdminOps"));
const AdminInvites = lazy(() => import("./pages/admin/AdminInvites"));

// Lazy loaded PM pages
const PMDashboard = lazy(() => import("./pages/pm/PMDashboard"));
const PMCreateJob = lazy(() => import("./pages/pm/PMCreateJob"));
const PMJobs = lazy(() => import("./pages/pm/PMJobs"));
const PMQuoteReview = lazy(() => import("./pages/pm/PMQuoteReview"));
const PMProperties = lazy(() => import("./pages/pm/PMProperties"));
const PMPropertyDetail = lazy(() => import("./pages/pm/PMPropertyDetail"));
const PMTenants = lazy(() => import("./pages/pm/PMTenants"));
const PMRequests = lazy(() => import("./pages/pm/PMRequests"));
const PMLandlords = lazy(() => import("./pages/pm/PMLandlords"));
const PMSettings = lazy(() => import("./pages/pm/PMSettings"));
const PMTradiesPage = lazy(() => import("./pages/pm/PMTradies"));
const PMDataImport = lazy(() => import("./pages/pm/PMDataImport"));
const PMExceptionQueue = lazy(() => import("./pages/pm/PMExceptionQueue"));
const PMCompliance = lazy(() => import("./pages/pm/PMCompliance"));
const PMInspections = lazy(() => import("./pages/pm/PMInspections"));
const PMArrears = lazy(() => import("./pages/pm/PMArrears"));
const PMLeases = lazy(() => import("./pages/pm/PMLeases"));
const PMCommunications = lazy(() => import("./pages/pm/PMCommunications"));
const PMOnboarding = lazy(() => import("./pages/pm/PMOnboarding"));

// Lazy loaded tradie onboarding
const TradieOnboarding = lazy(() => import("./pages/tradie/TradieOnboarding"));

// Lazy loaded public pages with tokens
const TenantRequestPage = lazy(() => import("./pages/TenantRequestPage"));
const TenantRequestTrackingPage = lazy(() => import("./pages/TenantRequestTrackingPage"));
const LandlordApprovalPage = lazy(() => import("./pages/LandlordApprovalPage"));
const QuoteSelectionPage = lazy(() => import("./pages/QuoteSelectionPage"));
const LeaseRenewalResponsePage = lazy(() => import("./pages/LeaseRenewalResponsePage"));
const InviteRedeemPage = lazy(() => import("./pages/InviteRedeemPage"));

// Lazy loaded tenant portal pages
const TenantLoginPage = lazy(() => import("./pages/tenant/TenantLoginPage"));
const TenantRegisterPage = lazy(() => import("./pages/tenant/TenantRegisterPage"));
const TenantResetPasswordPage = lazy(() => import("./pages/tenant/TenantResetPasswordPage"));
const TenantDashboard = lazy(() => import("./pages/tenant/TenantDashboard"));
const TenantNewRequest = lazy(() => import("./pages/tenant/TenantNewRequest"));
const TenantRequestDetail = lazy(() => import("./pages/tenant/TenantRequestDetail"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full" />
  </div>
);

/**
 * React Query client with performance-optimized defaults
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: 'always',
      refetchOnReconnect: true,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
    mutations: {
      retry: 1,
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode; allowedRoles?: string[] }> = ({ children, allowedRoles }) => {
  const { user, isLoading, userRole, isApproved } = useAuth();
  
  if (isLoading) {
    return <PageLoader />;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
    if (userRole === 'admin') return <Navigate to="/admin" replace />;
    if (userRole === 'tradie') return <Navigate to="/tradie" replace />;
    if (userRole === 'property_manager') return <Navigate to="/pm" replace />;
    if (userRole === 'tenant') return <Navigate to="/tenant" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  // Tenants are auto-approved via register_tenant_from_invite RPC; skip approval screen
  // Guard: if userRole is still null (profile loading/failed), don't block with approval screen
  if (userRole && userRole !== 'admin' && userRole !== 'tenant' && !isApproved) {
    return <PendingApprovalScreen />;
  }
  
  return <>{children}</>;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/invite/:code" element={<InviteRedeemPage />} />
                <Route path="/request/:token" element={<FlowErrorBoundary flowName="maintenance-request" message="Failed to load the maintenance request form. Please try again."><TenantRequestPage /></FlowErrorBoundary>} />
                <Route path="/request/track/:token" element={<TenantRequestTrackingPage />} />
                <Route path="/approve/:token" element={<FlowErrorBoundary flowName="landlord-approval" message="Failed to load the approval page. Please try again."><LandlordApprovalPage /></FlowErrorBoundary>} />
                <Route path="/select-quote/:token" element={<FlowErrorBoundary flowName="quote-selection" message="Failed to load the quote selection page. Please try again."><QuoteSelectionPage /></FlowErrorBoundary>} />
                <Route path="/renewal/:token" element={<FlowErrorBoundary flowName="lease-renewal" message="Failed to load the renewal page. Please try again."><LeaseRenewalResponsePage /></FlowErrorBoundary>} />

                {/* Tenant Auth (public) */}
                <Route path="/tenant/login" element={<TenantLoginPage />} />
                <Route path="/tenant/register" element={<TenantRegisterPage />} />
                <Route path="/tenant/reset-password" element={<TenantResetPasswordPage />} />
                
                {/* Tenant Dashboard (protected) */}
                <Route
                  path="/tenant"
                  element={
                    <ProtectedRoute allowedRoles={['tenant']}>
                      <TenantDashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<TenantDashboard />} />
                  <Route path="request/new" element={<TenantNewRequest />} />
                  <Route path="request/:id" element={<TenantRequestDetail />} />
                </Route>
                
                {/* Customer Dashboard */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['customer']}>
                      <CustomerDashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<CustomerDashboard />} />
                  <Route path="jobs/new" element={<NewJobPage />} />
                  <Route path="jobs/:id" element={<JobDetailsPage />} />
                </Route>

                {/* Tradie Dashboard */}
                {/* Tradie Onboarding (outside dashboard layout) */}
                <Route
                  path="/tradie/onboarding"
                  element={
                    <ProtectedRoute allowedRoles={['tradie']}>
                      <TradieOnboarding />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/tradie"
                  element={
                    <ProtectedRoute allowedRoles={['tradie']}>
                      <TradieDashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<TradieDashboard />} />
                  <Route path="jobs" element={<TradieJobFeed />} />
                  <Route path="jobs/:id" element={<JobDetailsPage />} />
                  <Route path="my-jobs" element={<TradieMyJobs />} />
                  <Route path="earnings" element={<TradieEarnings />} />
                  <Route path="settings" element={<TradieSettings />} />
                </Route>

                {/* PM Onboarding (outside dashboard layout) */}
                <Route
                  path="/pm/onboarding"
                  element={
                    <ProtectedRoute allowedRoles={['property_manager']}>
                      <PMOnboarding />
                    </ProtectedRoute>
                  }
                />

                {/* Property Manager Dashboard */}
                <Route
                  path="/pm"
                  element={
                    <ProtectedRoute allowedRoles={['property_manager']}>
                      <PMDashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<PMDashboard />} />
                  <Route path="create-job" element={<PMCreateJob />} />
                  <Route path="requests" element={<PMRequests />} />
                  <Route path="jobs" element={<PMJobs />} />
                  <Route path="quotes" element={<PMQuoteReview />} />
                  <Route path="properties" element={<PMProperties />} />
                  <Route path="properties/:id" element={<PMPropertyDetail />} />
                  <Route path="tenants" element={<PMTenants />} />
                  <Route path="landlords" element={<PMLandlords />} />
                  <Route path="tradies" element={<PMTradiesPage />} />
                  <Route path="import" element={<PMDataImport />} />
                  <Route path="exceptions" element={<PMExceptionQueue />} />
                  <Route path="compliance" element={<PMCompliance />} />
                  <Route path="inspections" element={<PMInspections />} />
                  <Route path="arrears" element={<PMArrears />} />
                  <Route path="leases" element={<PMLeases />} />
                  <Route path="communications" element={<PMCommunications />} />
                  <Route path="settings" element={<PMSettings />} />
                </Route>

                {/* Admin Dashboard */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminDashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="jobs" element={<AdminJobs />} />
                  <Route path="job-events" element={<AdminJobEvents />} />
                  <Route path="alerts" element={<AdminAlerts />} />
                  <Route path="invites" element={<AdminInvites />} />
                  <Route path="ops" element={<AdminOps />} />
                  <Route path="pricing" element={<AdminPricing />} />
                  <Route path="enterprise-pricing" element={<AdminEnterprisePricing />} />
                  <Route path="reports" element={<AdminReports />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
